// src/components/dialogs/ClaimDetailsDialog.jsx
import React, { useMemo, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Avatar,
  Chip,
  IconButton,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";

import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PaymentsIcon from "@mui/icons-material/Payments";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TagIcon from "@mui/icons-material/Tag";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";

import { formatDateTime, moneyINR } from "../../../utils/helpers";
import { BASE_URL } from "../../config/api";

/* ─────────────────────────────────────────────
   Keyframe — sheet entry animation
───────────────────────────────────────────── */
const ANIM = `
  @keyframes sheetUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulseDot {
    0%, 100% { box-shadow: 0 0 0 0   rgba(37,99,235,0.4); }
    50%       { box-shadow: 0 0 0 6px rgba(37,99,235,0);   }
  }
`;

/* ─────────────────────────────────────────────
   STATUS FLOW — UNCHANGED
───────────────────────────────────────────── */
const CLAIM_STATUS_TEMPLATE = [
  { key: "raised",        label: "Claim Raised",   description: "Your claim has been submitted successfully." },
  { key: "otp_sent",      label: "OTP Verified",   description: "OTP verification completed." },
  { key: "estimate_sent", label: "Estimate Sent",  description: "Inspection completed. Estimate shared." },
  { key: "completed",     label: "Completed",      description: "Claim completed successfully." },
  { key: "rejected",      label: "Rejected",       description: "Claim has been rejected." },
];

/* ─────────────────────────────────────────────
   Status header chip
───────────────────────────────────────────── */
function StatusHeaderChip({ status }) {
  const cfg = (() => {
    switch (status) {
      case "completed":
        return { label: "Completed", icon: <VerifiedIcon />,    bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
      case "rejected":
        return { label: "Rejected",  icon: <CancelIcon />,     bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
      case "approved":
        return { label: "Approved",  icon: <VerifiedIcon />,    bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
      case "otp_sent":
        return { label: "OTP Sent",  icon: <RadioButtonCheckedIcon />, bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
      case "pending":
        return { label: "Pending",   icon: <HourglassTopIcon />, bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" };
      default:
        return { label: status?.replace(/_/g, " ") ?? "—", icon: <HourglassTopIcon />, bg: "#f8fafc", color: "#475569", border: "#e2e8f0" };
    }
  })();

  return (
    <Chip
      icon={React.cloneElement(cfg.icon, {
        sx: { fontSize: "12px !important", color: `${cfg.color} !important` },
      })}
      label={cfg.label}
      size="small"
      sx={{
        height: 22,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        borderRadius: 0.2,
        "& .MuiChip-icon": { ml: "5px" },
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Section card with icon header
───────────────────────────────────────────── */
function AppCard({ icon, title, children }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0.2,
        border: "1px solid #e8f0fe",
        bgcolor: "#fff",
        boxShadow: "0 1px 5px rgba(0,0,0,0.05)",
      }}
    >
      <CardContent sx={{ p: { xs: "14px !important", sm: "16px !important" } }}>
        {(icon || title) && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.4}>
              {icon && (
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 0.2,
                    bgcolor: "#f0fdf4",
                    border: "1px solid #dcfce7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {React.cloneElement(icon, { sx: { fontSize: 14, color: "#16a34a" } })}
                </Box>
              )}
              {title && (
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 12, sm: 13 }, color: "#111827" }}
                >
                  {title}
                </Typography>
              )}
            </Stack>
            <Divider sx={{ borderColor: "#f1f5f9", mb: 1.2 }} />
          </>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────────────────────────
   Label / value row
───────────────────────────────────────────── */
function Row({ label, value, bold, mono }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      sx={{ py: 0.55 }}
    >
      <Typography
        sx={{
          fontSize: { xs: 11, sm: 12 },
          color: "#94a3b8",
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: 11, sm: 12 },
          fontWeight: bold ? 700 : 600,
          color: "#111827",
          textAlign: "right",
          fontFamily: mono ? "monospace" : "inherit",
          wordBreak: "break-all",
        }}
      >
        {value ?? "—"}
      </Typography>
    </Stack>
  );
}

/* ─────────────────────────────────────────────
   Main dialog
───────────────────────────────────────────── */
export default function ClaimDetailsDialog({ open, onClose, claim }) {
  const [tab, setTab] = useState(0);

  /* ── Business logic — UNCHANGED ── */
  const inspectionReport = useMemo(() => {
    try {
      return JSON.parse(claim.inspection_report || "[]");
    } catch {
      return [];
    }
  }, [claim]);

  if (!claim) return null;

  const currentIndex = CLAIM_STATUS_TEMPLATE.findIndex(
    (s) => s.key === claim.status
  );

  const employee = claim.assignment?.employee;

  return (
    <>
      <style>{ANIM}</style>

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            borderRadius: "14px 14px 0 0",
            position: "fixed",
            bottom: 0,
            maxHeight: { xs: "92svh", sm: "90vh" },
            boxShadow: "0 -8px 40px rgba(0,0,0,0.14)",
            animation: "sheetUp 0.28s cubic-bezier(0.32,0.72,0,1) both",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            paddingLeft: "env(safe-area-inset-left)",
            paddingRight: "env(safe-area-inset-right)",
          },
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(3px)",
            bgcolor: "rgba(0,0,0,0.35)",
          },
        }}
      >

        {/* ── Drag handle ── */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, flexShrink: 0 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: "#e2e8f0" }} />
        </Box>

        {/* ── Sticky header ── */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: 1.2,
            pb: 0,
            bgcolor: "#fff",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>

            {/* Left: title + id + status */}
            <Box flex={1} minWidth={0}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.4}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 15, sm: 16 },
                    color: "#111827",
                    lineHeight: 1.2,
                  }}
                >
                  Claim Details
                </Typography>
                <StatusHeaderChip status={claim.status} />
              </Stack>

              <Stack direction="row" spacing={0.6} alignItems="center">
                <TagIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                <Typography
                  sx={{
                    fontSize: { xs: 10, sm: 11 },
                    color: "#94a3b8",
                    fontFamily: "monospace",
                    fontWeight: 600,
                  }}
                >
                  {claim.id}
                </Typography>
              </Stack>
            </Box>

            {/* Close button — 36×36 touch target */}
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 0.2,
                width: 34,
                height: 34,
                flexShrink: 0,
                "&:hover": { bgcolor: "#f1f5f9" },
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: "#475569" }} />
            </IconButton>
          </Stack>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              mt: 1.2,
              minHeight: 38,
              borderBottom: "1px solid #f1f5f9",
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: { xs: 12, sm: 13 },
                fontWeight: 600,
                minHeight: 38,
                px: { xs: 1.5, sm: 2 },
                color: "#94a3b8",
                "&.Mui-selected": { color: "#16a34a" },
              },
              "& .MuiTabs-indicator": {
                bgcolor: "#16a34a",
                height: 2.5,
                borderRadius: "2px 2px 0 0",
              },
            }}
          >
            <Tab label="Details" />
            <Tab label="Timeline" />
          </Tabs>
        </Box>

        {/* ── Scrollable body ── */}
        <Box
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            flexGrow: 1,
            bgcolor: "#f8fafc",
            px: { xs: 1.5, sm: 2 },
            pt: 1.8,
            pb: "max(24px, env(safe-area-inset-bottom, 24px))",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#e2e8f0", borderRadius: 99 },
          }}
        >

          {/* ══════════════
              DETAILS TAB
          ══════════════ */}
          {tab === 0 && (
            <Stack spacing={1.5}>

              {/* Device */}
              <AppCard icon={<PhoneIphoneIcon />} title="Device">
                <Row label="Model" value={claim.device?.model || "—"} />
                <Divider sx={{ borderColor: "#f8fafc", my: 0.3 }} />
                <Row label="IMEI" value={claim.device?.imei1 || "—"} mono />
              </AppCard>

              {/* Customer */}
              <AppCard icon={<PersonIcon />} title="Customer">
                <Row label="Name"   value={claim.customer?.name} />
                <Divider sx={{ borderColor: "#f8fafc", my: 0.3 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.55}>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: "#94a3b8", fontWeight: 500 }}>
                    Mobile
                  </Typography>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600, color: "#111827" }}>
                      {claim.customer?.mobile ?? "—"}
                    </Typography>
                    {claim.customer?.mobile && (
                      <Box
                        component="a"
                        href={`tel:${claim.customer.mobile}`}
                        sx={{ display: "flex", color: "#16a34a", lineHeight: 0 }}
                      >
                        <PhoneIcon sx={{ fontSize: 13 }} />
                      </Box>
                    )}
                  </Stack>
                </Stack>
                <Divider sx={{ borderColor: "#f8fafc", my: 0.3 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.55}>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: "#94a3b8", fontWeight: 500 }}>
                    Email
                  </Typography>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Typography
                      sx={{
                        fontSize: { xs: 11, sm: 12 },
                        fontWeight: 600,
                        color: "#111827",
                        maxWidth: { xs: 160, sm: 220 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {claim.customer?.email ?? "—"}
                    </Typography>
                    {claim.customer?.email && (
                      <Box
                        component="a"
                        href={`mailto:${claim.customer.email}`}
                        sx={{ display: "flex", color: "#2563eb", lineHeight: 0 }}
                      >
                        <EmailIcon sx={{ fontSize: 13 }} />
                      </Box>
                    )}
                  </Stack>
                </Stack>
              </AppCard>

              {/* Claim Information */}
              <AppCard icon={<AssignmentIcon />} title="Claim Information">
                <Row label="Issue"   value={claim.issue_description} />
                <Divider sx={{ borderColor: "#f8fafc", my: 0.3 }} />

                {/* Claim type with icon */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.55}>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: "#94a3b8", fontWeight: 500 }}>
                    Type
                  </Typography>
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    {claim.claim_type === "pickup" ? (
                      <LocalShippingIcon sx={{ fontSize: 13, color: "#7c3aed" }} />
                    ) : (
                      <StorefrontIcon sx={{ fontSize: 13, color: "#0891b2" }} />
                    )}
                    <Typography
                      sx={{
                        fontSize: { xs: 11, sm: 12 },
                        fontWeight: 600,
                        color: claim.claim_type === "pickup" ? "#7c3aed" : "#0891b2",
                      }}
                    >
                      {claim.claim_type === "pickup" ? "Pickup" : "Drop-off"}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider sx={{ borderColor: "#f8fafc", my: 0.3 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.55}>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: "#94a3b8", fontWeight: 500 }}>
                    Created
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <AccessTimeIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                    <Typography sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 600, color: "#374151" }}>
                      {formatDateTime(claim.created_at)}
                    </Typography>
                  </Stack>
                </Stack>
              </AppCard>

              {/* Assigned Executive */}
              {employee && (
                <AppCard icon={<SupportAgentIcon />} title="Assigned Executive">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={employee.photo_url}
                      sx={{
                        width: { xs: 44, sm: 48 },
                        height: { xs: 44, sm: 48 },
                        border: "2px solid #dcfce7",
                        flexShrink: 0,
                      }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography
                        sx={{
                          fontSize: { xs: 13, sm: 14 },
                          fontWeight: 700,
                          color: "#111827",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {employee.first_name} {employee.last_name}
                      </Typography>
                      <Stack direction="row" spacing={0.6} alignItems="center" mt={0.3}>
                        <PhoneIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                        <Typography
                          sx={{
                            fontSize: { xs: 11, sm: 12 },
                            color: "#64748b",
                            fontFamily: "monospace",
                          }}
                        >
                          {employee.official_phone}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box
                      component="a"
                      href={`tel:${employee.official_phone}`}
                      sx={{ display: "flex", flexShrink: 0 }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 0.2,
                          bgcolor: "#f0fdf4",
                          border: "1px solid #dcfce7",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PhoneIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                      </Box>
                    </Box>
                  </Stack>
                </AppCard>
              )}

              {/* Payment Summary */}
              <AppCard icon={<PaymentsIcon />} title="Payment Summary">
                <Stack spacing={0}>
                  {inspectionReport.map((item, idx) => (
                    <React.Fragment key={idx}>
                      <Row label={item.label} value={moneyINR(item.amount)} />
                      {idx < inspectionReport.length - 1 && (
                        <Divider sx={{ borderColor: "#f8fafc" }} />
                      )}
                    </React.Fragment>
                  ))}
                </Stack>

                {inspectionReport.length > 0 && (
                  <Divider sx={{ my: 1.2, borderColor: "#e8f0fe" }} />
                )}

                {/* Total row — highlighted */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    bgcolor: "#f0fdf4",
                    border: "1px solid #dcfce7",
                    borderRadius: 0.2,
                    px: 1.2,
                    py: 0.9,
                  }}
                >
                  <Typography
                    sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: "#15803d" }}
                  >
                    Total Payable
                  </Typography>
                  <Typography
                    sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 800, color: "#15803d" }}
                  >
                    {moneyINR(claim.payable_amount)}
                  </Typography>
                </Stack>

                {/* Pay Now CTA */}
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  endIcon={<OpenInNewIcon sx={{ fontSize: "15px !important" }} />}
                  onClick={() => window.open(claim.payment_link, "_blank")}
                  sx={{
                    mt: 1.4,
                    borderRadius: 0.2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: 13, sm: 14 },
                    minHeight: 46,
                    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                    boxShadow: "0 4px 14px rgba(22,163,74,0.32)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                      boxShadow: "0 6px 18px rgba(22,163,74,0.40)",
                    },
                    "&:active": { boxShadow: "0 2px 6px rgba(22,163,74,0.2)" },
                    transition: "all 0.18s ease",
                  }}
                >
                  Pay Now
                </Button>
              </AppCard>

              {/* Photos */}
              {claim.photos?.length > 0 && (
                <AppCard icon={<PhotoLibraryIcon />} title="Photos">
                  <Grid container spacing={1}>
                    {claim.photos.map((photo) => (
                      <Grid item xs={4} key={photo.id}>
                        <Box
                          component="img"
                          src={`https://zoho.goelectronix.in/storage/${photo.photo_path}`}
                          sx={{
                            width: "100%",
                            height: { xs: 80, sm: 96 },
                            borderRadius: 0.2,
                            objectFit: "cover",
                            border: "1px solid #e8f0fe",
                            display: "block",
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            "@media (hover: hover)": {
                              "&:hover": {
                                transform: "scale(1.04)",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              },
                            },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AppCard>
              )}

            </Stack>
          )}

          {/* ══════════════
              TIMELINE TAB
          ══════════════ */}
          {tab === 1 && (
            <AppCard icon={<AssignmentIcon />} title="Claim Progress">
              <Stack spacing={0}>
                {CLAIM_STATUS_TEMPLATE.map((step, index) => {
                  const isCompleted = index < currentIndex;
                  const isActive    = index === currentIndex;
                  const isLast      = index === CLAIM_STATUS_TEMPLATE.length - 1;

                  return (
                    <Stack key={step.key} direction="row" spacing={1.5}>

                      {/* Left: icon + connector line */}
                      <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                        {/* Step icon */}
                        <Box
                          sx={{
                            mt: "2px",
                            width: 22,
                            height: 22,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {isCompleted ? (
                            <CheckCircleIcon sx={{ fontSize: 22, color: "#16a34a" }} />
                          ) : isActive ? (
                            <RadioButtonCheckedIcon
                              sx={{
                                fontSize: 22,
                                color: "#2563eb",
                                animation: "pulseDot 1.8s ease-in-out infinite",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            <RadioButtonUncheckedIcon
                              sx={{ fontSize: 22, color: "#e2e8f0" }}
                            />
                          )}
                        </Box>

                        {/* Connector — coloured if completed segment */}
                        {!isLast && (
                          <Box
                            sx={{
                              width: 2,
                              flex: 1,
                              minHeight: 32,
                              bgcolor: isCompleted ? "#bbf7d0" : "#f1f5f9",
                              borderRadius: 1,
                              my: 0.4,
                            }}
                          />
                        )}
                      </Stack>

                      {/* Right: label + description */}
                      <Box pb={isLast ? 0 : 2} pt="1px">
                        <Typography
                          sx={{
                            fontSize: { xs: 12, sm: 13 },
                            fontWeight: isActive ? 700 : isCompleted ? 600 : 500,
                            color: isCompleted
                              ? "#15803d"
                              : isActive
                              ? "#1d4ed8"
                              : "#94a3b8",
                            lineHeight: 1.3,
                          }}
                        >
                          {step.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: 10, sm: 11 },
                            color: isActive ? "#64748b" : "#94a3b8",
                            mt: 0.3,
                            lineHeight: 1.5,
                          }}
                        >
                          {step.description}
                        </Typography>
                      </Box>

                    </Stack>
                  );
                })}
              </Stack>
            </AppCard>
          )}

        </Box>
      </Dialog>
    </>
  );
}